import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, CheckCircle2, ShieldAlert } from 'lucide-react';

export const SaaSAgreement = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto p-6 md:p-12 space-y-8 text-gray-800">
      <button 
        id="btn-saas-back"
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-500 hover:text-gray-900 mb-6 font-bold text-xs uppercase tracking-wider cursor-pointer"
      >
        <ArrowLeft size={16} className="mr-2" />
        Go Back
      </button>

      <div className="border-b border-gray-200 pb-6 space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 border border-purple-100 rounded-full text-[10px] font-black uppercase tracking-widest text-purple-700">
          <Building2 size={12} />
          Commercial Subscription Terms
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight uppercase italic">Software as a Service (SaaS) Agreement</h1>
        <p className="text-xs text-gray-500 font-medium">Last Updated: August 26, 2026</p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">1. Agreement Overview & Contracting Parties</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          This Software as a Service Agreement ("Agreement") is entered into by and between <strong>Data Driven Bookkeeping LLC</strong>, doing business as <strong>GrowEzi AI</strong> ("Company," "Licensor," "we," or "us"), and the subscribing entity or individual ("Customer," "Licensee," "you," or "your") accessing or subscribing to the <strong>TipValue AI</strong> platform ("Product" or "Service").
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">2. Subscription License & Access Rights</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          Subject to the terms and conditions of this Agreement and active payment of applicable subscription fees, Company grants Customer a limited, non-exclusive, non-transferable, non-sublicensable right to access and use TipValue AI during the subscription term for Customer's internal restaurant and hospitality operations.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">3. Customer Data & System Administration</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          Customer retains all right, title, and interest in and to all data input into TipValue AI, including POS transaction logs, employee rosters, and shift hours ("Customer Data"). Company processes Customer Data solely to perform automated calculations, facilitate employee sign-offs, and generate payroll exports requested by Customer.
        </p>
        <p className="text-sm leading-relaxed text-gray-600">
          Customer is solely responsible for the accuracy, legality, and integrity of all Customer Data, configured tip pool ratios, role multipliers, and employee wage classifications.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">4. Fees, Billing & Payment Terms</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          Customer agrees to pay all subscription fees specified in the active tier plan (e.g., Growth Pro monthly or annual plans). Subscriptions auto-renew unless cancelled prior to the end of the current billing cycle. All payments are non-refundable except where explicitly required by law.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">5. Intellectual Property Rights</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          Data Driven Bookkeeping LLC d/b/a GrowEzi AI retains all right, title, and interest in and to TipValue AI, including all associated software algorithms, user interfaces, documentation, trade secrets, and intellectual property. Customer shall not reverse engineer, decompile, or create derivative works of the Service.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">6. Disclaimer of Warranties ("AS IS" & "AS AVAILABLE")</h2>
        <div className="p-5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-700 text-xs leading-relaxed font-mono uppercase space-y-2">
          <p className="font-bold text-gray-900">
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.
          </p>
          <p>
            COMPANY EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, ACCURACY, AND NON-INFRINGEMENT. COMPANY DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">7. Limitation of Liability</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          In no event shall Company or its suppliers be liable for any indirect, special, incidental, punitive, or consequential damages (including loss of profits, loss of data, business interruption, or labor dispute liabilities) arising out of or related to this Agreement. Company's total cumulative liability under this Agreement shall not exceed the total fees actually paid by Customer to Company during the twelve (12) months preceding the claim.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">8. Term & Termination</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          This Agreement remains in effect for the duration of Customer's subscription. Either party may terminate this Agreement if the other party materially breaches any provision and fails to cure such breach within thirty (30) days of receipt of written notice.
        </p>
      </div>

      <div className="space-y-4 border-t border-gray-200 pt-6">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">9. Legal Notices</h2>
        <p className="text-sm text-gray-600">
          All legal notices under this Agreement shall be sent to:
        </p>
        <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-700 space-y-1">
          <p className="font-bold">Data Driven Bookkeeping LLC d/b/a GrowEzi AI</p>
          <p>Attention: Legal / Subscription Administration</p>
          <p>Email: <a href="mailto:admin@growezy.ai" className="text-blue-600 underline">admin@growezy.ai</a></p>
        </div>
      </div>
    </div>
  );
};
